import { useEffect } from 'react';
import { useRouter } from 'next/router';

const waitForElement = (id: string, timeoutMs: number): Promise<HTMLElement | null> => {
    return new Promise((resolve) => {
        const startTime = performance.now();
        let lastTop = 0;
        let stableFrames = 0;

        const check = () => {
            const el = document.getElementById(id);
            if (!el || el.offsetHeight === 0 || el.offsetWidth === 0) {
                if (performance.now() - startTime < timeoutMs) {
                    requestAnimationFrame(check);
                } else {
                    resolve(null);
                }
                return;
            }

            const currentTop = el.getBoundingClientRect().top;

            if (Math.abs(currentTop - lastTop) < 1) {
                stableFrames++;
            } else {
                stableFrames = 0;
            }

            lastTop = currentTop;

            if (stableFrames >= 10) {
                resolve(el);
            } else if (performance.now() - startTime < timeoutMs) {
                requestAnimationFrame(check);
            } else {
                resolve(el);
            }
        };

        check();
    });
};

const isVisible = (el: HTMLElement | null): el is HTMLElement =>
    !!el && el.offsetHeight > 0 && el.offsetWidth > 0;


export const useScrollToAnchor = () => {
    const router = useRouter();
    const MAX_WAIT_TIME_MS = 10000;

    const waitAndScrollToElement = async (id: string) => {
        const el = await waitForElement(id, MAX_WAIT_TIME_MS);
        if (isVisible(el)) {
            el.scrollIntoView();
        }
    };

    useEffect(() => {
        const handleHashChange = (url?: string) => {
            const hash = (url || window.location.hash)?.split('#')[1];
            if (hash) waitAndScrollToElement(hash);
        };

        handleHashChange();
        router.events.on('routeChangeComplete', handleHashChange);
        return () => {
            router.events.off('routeChangeComplete', handleHashChange);
        };
    }, [router.events]);
};
